/**
 * Roles — two independent axes, deliberately.
 *
 * The LADDER is `EDITOR (1) < MANAGER (2) < ADMIN (3)`; each rank passes every lower
 * check, so a guard is written once as `hasMinRole(role, 'MANAGER')` and never as a
 * list of role names.
 *
 * `VIEWER` is deliberately ABSENT from `ROLE_RANK`. Giving it even EDITOR rank would
 * satisfy `hasMinRole(role, 'EDITOR')` on every mutating route gated on it, leaving
 * read-only resting entirely on one hook. Off the ladder, every `hasMinRole` guard in
 * the system refuses it by construction — so a route added next year is closed without
 * anyone remembering the role exists. See TECHNICAL_DOCUMENT.md §4.
 */

export const ROLES = ['VIEWER', 'EDITOR', 'MANAGER', 'ADMIN'] as const;
export type Role = (typeof ROLES)[number];

/** The ladder. A role absent from this map fails every `hasMinRole` check. */
export const ROLE_RANK: Partial<Record<Role, number>> = {
  EDITOR: 1,
  MANAGER: 2,
  ADMIN: 3,
};

/** Human labels for the back office. */
export const ROLE_LABEL: Record<Role, string> = {
  VIEWER: 'Viewer',
  EDITOR: 'Editor',
  MANAGER: 'Manager',
  ADMIN: 'Administrator',
};

/** What each role is for, shown on the Users screen so the choice is not a guess. */
export const ROLE_DESCRIPTION: Record<Role, string> = {
  VIEWER: 'Reads every screen. Cannot change anything, anywhere.',
  EDITOR: 'Website content — pages, projects, services, team, testimonials, posts, media, navigation.',
  MANAGER: 'Everything an Editor can do, plus the store: products, inventory, stock, orders and enquiries.',
  ADMIN: 'Everything, plus users, site settings and the audit log.',
};

export function hasMinRole(role: string | null | undefined, min: Role): boolean {
  if (!role) return false;
  const have = ROLE_RANK[role as Role];
  const need = ROLE_RANK[min];
  if (have === undefined || need === undefined) return false;
  return have >= need;
}

/** True for the read-only account, which is off the ladder and judged separately. */
export function isReadOnlyRole(role: string | null | undefined): boolean {
  return role === 'VIEWER';
}

/**
 * Roles a signed-in administrator may assign from the Users screen.
 *
 * VIEWER is included here — unlike the DMS, this deployment is single-tenant, so a
 * Viewer is an ordinary account with no cross-tenant reach and is safe to hand out.
 */
export const ASSIGNABLE_ROLES = ['VIEWER', 'EDITOR', 'MANAGER', 'ADMIN'] as const;
